import { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { SliderInput } from './SliderInput';
import { calculateCompoundInterest } from '../../utils/calculations';
import { getTaxConfig, INVESTMENT_TYPES } from '../../utils/taxEngine';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend 
} from 'recharts';
import { GitCompare, Coins } from 'lucide-react';

export const StrategyComparison = () => {
  const { inputs, t, theme, language, formatMoney, currency } = useApp();
  const [altContribution, setAltContribution] = useState(inputs.monthlyContribution * 0.5);
  
  // Calculate comparison results
  const comparisonResults = useMemo(() => {
    const taxConfig = getTaxConfig(inputs.investmentType);
    const isUnitLinked = inputs.investmentType === INVESTMENT_TYPES.UNIT_LINKED;
    
    const baseParams = {
      initialDeposit: inputs.initialDeposit,
      monthlyContribution: inputs.monthlyContribution,
      annualInterestRate: inputs.annualInterestRate,
      years: inputs.years,
      compoundingFrequency: inputs.compoundingFrequency,
      contributionFrequency: inputs.contributionFrequency,
      taxRate: inputs.includeTaxes ? taxConfig.interestTaxRate : 0,
      taxOnEachPeriod: taxConfig.taxOnEachPeriod,
      adminFeeRate: isUnitLinked ? inputs.adminFeeRate : 0,
      pauseStartYear: inputs.pauseEnabled ? inputs.pauseStartYear : 0,
      pauseEndYear: inputs.pauseEnabled ? inputs.pauseEndYear : 0,
      useSimpleInterest: taxConfig.useSimpleInterest,
    };
    
    const altParams = {
      ...baseParams,
      monthlyContribution: altContribution,
    };
    
    const baseResult = calculateCompoundInterest(baseParams);
    const altResult = calculateCompoundInterest(altParams);
    
    return {
      current: baseResult,
      alternative: altResult,
    };
  }, [inputs, altContribution]);
  
  // Generate comparison chart data
  const chartData = useMemo(() => {
    const maxYears = Math.max(comparisonResults.current.yearlyBreakdown.length, comparisonResults.alternative.yearlyBreakdown.length);
    const data = [];
    
    for (let i = 0; i < maxYears; i++) {
      const currentYear = comparisonResults.current.yearlyBreakdown[i];
      const altYear = comparisonResults.alternative.yearlyBreakdown[i];
      
      data.push({
        year: i + 1,
        current: currentYear?.endBalance || comparisonResults.current.finalValue,
        alternative: altYear?.endBalance || 0,
      });
    }
    
    return data;
  }, [comparisonResults]);
  
  const isDark = theme === 'dark';
  
  // Currency suffix for slider
  const currencySuffix = currency === 'EUR' ? ' EUR' : ' RON';
  
  return (
    <Card 
      className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden"
      data-testid="strategy-comparison"
    >
      <CardHeader className="pb-4 border-b border-border/50">
        <CardTitle className="flex items-center gap-3 text-xl font-light tracking-tight">
          <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10">
            <GitCompare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          {t('strategyComparison')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {/* Alternative contribution input */}
        <div className="p-6 rounded-2xl bg-amber-100 dark:bg-amber-900/40 border-2 border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2 mb-4">
            <Coins className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <span className="text-sm font-semibold text-amber-800 dark:text-amber-200">
              {t('alternativeContribution')}
            </span>
          </div>
          <SliderInput
            label={t('monthlyContribution')}
            value={altContribution}
            onChange={setAltContribution}
            min={0}
            max={Math.max(inputs.monthlyContribution * 2, 10000)}
            step={50}
            suffix={currencySuffix}
            testId="alt-contribution"
          />
        </div>
        
        {/* Results comparison - Only 2 cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Current strategy */}
          <div className="p-5 rounded-2xl border-2 border-blue-300 dark:border-blue-700 bg-blue-100 dark:bg-blue-900/50">
            <div className="text-xs uppercase tracking-[0.12em] text-blue-700 dark:text-blue-300 font-semibold mb-2">
              {t('strategy1')}
            </div>
            <div className="text-2xl font-black tracking-tight text-blue-800 dark:text-blue-200">
              {formatMoney(comparisonResults.current.finalValue)}
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300 mt-2 font-medium">
              {inputs.years} {inputs.years === 1 ? 'an' : 'ani'} × {formatMoney(inputs.monthlyContribution)}/{language === 'ro' ? 'lună' : 'month'}
            </div>
          </div>
          
          {/* Alternative strategy */}
          <div className="p-5 rounded-2xl border-2 border-amber-300 dark:border-amber-700 bg-amber-100 dark:bg-amber-900/50">
            <div className="text-xs uppercase tracking-[0.12em] text-amber-700 dark:text-amber-300 font-semibold mb-2">
              {t('strategy2')}
            </div>
            <div className="text-2xl font-black tracking-tight text-amber-800 dark:text-amber-200">
              {formatMoney(comparisonResults.alternative.finalValue)}
            </div>
            <div className="text-sm text-amber-700 dark:text-amber-300 mt-2 font-medium">
              {inputs.years} {inputs.years === 1 ? 'an' : 'ani'} × {formatMoney(altContribution)}/{language === 'ro' ? 'lună' : 'month'}
            </div>
          </div>
        </div>
        
        {/* Comparison chart */}
        <div className="h-[250px] w-full mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorCurrent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorAlternative" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={isDark ? '#27272a' : '#e2e8f0'} 
                vertical={false}
              />
              <XAxis 
                dataKey="year" 
                stroke={isDark ? '#71717a' : '#94a3b8'}
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis 
                stroke={isDark ? '#71717a' : '#94a3b8'}
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                width={50}
              />
              <Tooltip 
                formatter={(value) => [formatMoney(value)]}
                labelFormatter={(label) => `${t('year')} ${label}`}
                contentStyle={{
                  backgroundColor: isDark ? '#18181b' : '#ffffff',
                  border: `1px solid ${isDark ? '#27272a' : '#e2e8f0'}`,
                  borderRadius: '12px',
                  padding: '12px',
                }}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '10px' }}
                formatter={(value) => (
                  <span className="text-xs text-muted-foreground">{value}</span>
                )}
              />
              <Area
                type="monotone"
                dataKey="current"
                name={t('strategy1')}
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#colorCurrent)"
                animationDuration={800}
              />
              <Area
                type="monotone"
                dataKey="alternative"
                name={t('strategy2')}
                stroke="#f59e0b"
                strokeWidth={2}
                fill="url(#colorAlternative)"
                animationDuration={800}
                animationBegin={200}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default StrategyComparison;
